import React, { useEffect, useLayoutEffect, useRef } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';
import clsx from 'clsx';
import 'intl-tel-input/build/css/intlTelInput.css';
import intlTelInput from 'intl-tel-input';
import {
  convertPathToDotNotation,
  createObjectFromDotNotation,
  getCountryCodeFromMetaTag,
  getPageLocale,
  telInputLocaleSearch,
  vrMandatoryField,
  vrPhoneNumber,
} from '@msc-b2c/shared-utils';
import { ValidationRulesContainer } from '@msc-b2c/interfaces/types';

type PhoneInputProps = {
  label: string;
  placeholder: string;
  name: string;
  prefixName: string;
  index: number;
  control: Control<any>;
  errors: FieldErrors;
  clearErrors: UseFormClearErrors<any>;
  setValue: UseFormSetValue<any>;
  disabled?: boolean;
  isLowerLabel?: boolean;
  validationRulesContainer?: ValidationRulesContainer[];
  additionalValidationObj?: any;
  register: UseFormRegister<any>;
  trigger: any;
  context?: any;
};

export const PhoneInput = ({
  label,
  placeholder,
  name,
  prefixName,
  index,
  control,
  errors,
  clearErrors,
  setValue,
  disabled,
  isLowerLabel,
  validationRulesContainer,
  additionalValidationObj,
  register,
  trigger,
  context,
}: PhoneInputProps) => {
  const isError = convertPathToDotNotation(errors, name);
  const isErrorClass = isError ? 'error-input' : '';
  const isDisabled = disabled ? 'disabled-input' : '';
  const labelClass = isLowerLabel ? 'cmp-input__lowerlabel' : 'cmp-input__upperlabel';

  return (
    <div className={`text ${labelClass} ${isErrorClass} ${isDisabled}`}>
      <div className="cmp-form-text tel">
        {!isLowerLabel && (
          <label
            className={clsx('cmp-label', isErrorClass)}
            htmlFor={`${name}-${index}`}
          >
            {label}
          </label>
        )}
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <IntlTelInputWrapper
              field={field}
              name={name}
              prefixName={prefixName}
              index={index}
              clearErrors={clearErrors}
              setValue={setValue} // Pass setValue to wrapper
              isError={!!isError}
              disabled={disabled}
              label={label}
              placeholder={placeholder}
              errors={errors}
              validationRulesContainer={validationRulesContainer}
              additionalValidationObj={additionalValidationObj}
              register={register}
              trigger={trigger}
              context={context}
            />
          )}
        />
        {isError && (
          <div className="error-list-validation">
            <div>{isError.message?.toString()}</div>
          </div>
        )}
      </div>
    </div>
  );
};

type IntlTelInputWrapperProps = {
  field: any;
  name: string;
  prefixName: string;
  index: number;
  clearErrors: UseFormClearErrors<any>;
  setValue: UseFormSetValue<any>; // Add setValue prop
  isError: boolean;
  disabled?: boolean;
  label: string;
  placeholder: string;
  errors: FieldErrors;
  validationRulesContainer?: ValidationRulesContainer[];
  additionalValidationObj?: any;
  register: UseFormRegister<any>;
  trigger: any;
  context?: any;
};

const E164_MAX_DIGITS = 15;

const IntlTelInputWrapper = ({
  field,
  name,
  prefixName,
  index,
  clearErrors,
  setValue,
  isError,
  disabled,
  placeholder,
  errors,
  validationRulesContainer,
  additionalValidationObj,
  register,
  trigger,
  context,
}: IntlTelInputWrapperProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const itiRef = useRef<any>(null);
  const hasBlurredRef = useRef<boolean>(false);

  useEffect(() => {
    if (inputRef.current) {
      itiRef.current = intlTelInput(inputRef.current, {
        strictMode: true,
        initialCountry: getCountryCodeFromMetaTag(),
        validationNumberType: null,
        i18n: telInputLocaleSearch[getPageLocale()],
        separateDialCode: true,
        utilsScript: 'https://cdn.jsdelivr.net/npm/intl-tel-input@24.8.2/build/js/utils.js',
      });
      (inputRef.current as any)._itiInitialized = true; // Prevent double initialization

      inputRef.current.addEventListener('countrychange', handleCountryChange);
      inputRef.current.addEventListener('input', handleChange);

      if (field.value) {
        itiRef.current.setNumber(field.value);
      } else {
        const initialNumber = itiRef.current.getNumber();
        field.onChange(initialNumber);
      }

      const initialDialCode = itiRef.current.getSelectedCountryData().dialCode;
      setValue(prefixName, initialDialCode);

      // The `utilsScript` (libphonenumber-based validation data) loads
      // asynchronously from a CDN. Any validation that runs before it
      // finishes (e.g. an automatic trigger right after a prefilled value)
      // only has access to a permissive fallback check, so it can report an
      // invalid number (e.g. a fake NANP area code like "555") as valid.
      // `iti.promise` resolves once the script is ready, so re-validate the
      // current number then to catch up with the definitive result — the
      // same result a later manual blur would already reflect.
      itiRef.current.promise?.then(
        () => {
          if (itiRef.current?.getNumber()) {
            void trigger(name);
          }
        },
        () => {
          // ignore failures loading the utils script; validation simply
          // falls back to the basic/permissive check in that case
        }
      );
    }

    return () => {
      if (itiRef.current) {
        inputRef.current?.removeEventListener('input', handleChange);
        itiRef.current.destroy();
      }
    };
  }, [disabled]);

  // Sync programmatic react-hook-form value changes (e.g. prefill) to the
  // intlTelInput DOM. The mount effect above only calls setNumber at init time;
  // without this, itiRef.current.getNumber() returns an empty number when
  // validateNumber() runs after a programmatic setValue, causing a false
  // "mandatory field" validation error. useLayoutEffect ensures this runs
  // before react-hook-form's validation re-render triggered by trigger().
  useLayoutEffect(() => {
    if (itiRef.current && field.value) {
      itiRef.current.setNumber(field.value);
      const dialCode = itiRef.current.getSelectedCountryData().dialCode;
      setValue(prefixName, dialCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value]);

  const handleCountryChange = () => {
    const number = itiRef.current.getNumber();
    field.onChange(number);

    const dialCode = itiRef.current.getSelectedCountryData().dialCode;
    setValue(prefixName, dialCode);

    if (hasBlurredRef.current) {
      trigger(name);
    }
  };

  const handleBlur = () => {
    hasBlurredRef.current = true;
    const number = itiRef.current.getNumber();
    field.onChange(number);
    field.onBlur();
    trigger(name);
    if (context) {
      context.setGuestFormData({
        ...context.guestFormData,
        ...createObjectFromDotNotation(name, number),
      });
    }
  };

  const handleChange = () => {
    // Never let the number grow past what the booking APIs accept
    const { dialCode } = itiRef.current.getSelectedCountryData();
    const maxDigits = E164_MAX_DIGITS - (dialCode?.length ?? 0);
    itiRef.current.maxCoreNumberLength = Math.min(
      itiRef.current.maxCoreNumberLength ?? maxDigits,
      maxDigits
    );

    const number = itiRef.current.getNumber();
    field.onChange(number);
    if (hasBlurredRef.current) {
      trigger(name);
    }
  };

  const validateNumber = () => {
    if (itiRef.current) {
      let errorMessage: string | boolean = true;
      const number = itiRef.current.getNumber();
      validationRulesContainer?.some((rule: ValidationRulesContainer) => {
        if (rule?.validationRules === 'vr-mandatory-field') {
          if (!vrMandatoryField(number)) {
            errorMessage = rule.validationRuleErrorMessage;
            return true;
          }
        } else if (rule?.validationRules === 'vr-phone-number') {
          if (!vrPhoneNumber(itiRef.current)) {
            errorMessage = rule.validationRuleErrorMessage;
            return true;
          }
        } else if (rule?.validationRules === 'vr-emergency-phone-number') {
          if (additionalValidationObj?.passengerData?.passengers) {
            let passengersData;
            passengersData = additionalValidationObj?.passengerData?.passengers;
            //check if exist a passenger with phone that is equals to the emergency contact phone
            passengersData = passengersData?.filter((passenger: { phone: string }) => {
              return passenger.phone === itiRef.current.getNumber();
            });
            if (passengersData.length > 0) {
              errorMessage = rule.validationRuleErrorMessage;
              return true;
            }
          }
        } else {
          clearErrors(name);
          return true;
        }
        return false;
      });
      return errorMessage;
    }
  };

  return (
    <input
      {...register(name, {
        validate: () => {
          return validateNumber();
        },
      })}
      ref={inputRef}
      name={name}
      id={`${name}-${index}`}
      type="tel"
      className={clsx('cmp-form-text__text tel-input-not-editorial iti__tel-input', {
        'error-input': convertPathToDotNotation(errors, name),
      })}
      inputMode="numeric" // Show numeric keyboard on mobile devices
      placeholder={placeholder}
      disabled={disabled}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={() => clearErrors(name)}
      aria-invalid={isError ? 'true' : 'false'}
      defaultValue={field.value}
    />
  );
};
